"use client";

import { EmailForm } from "@/components/forms/auth/email-form";
import { VerifyForm } from "@/components/forms/auth/verify-form";
import { LoadingSpinner } from "@/components/shared/loading-spinner";
import { T } from "@/components/shared/T";
import { useNavigate } from "@/hooks";
import { AuthStatus, useAuthContext } from "@/providers/auth-provider";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

function Carousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = [
    "/images/login/carousel-1.png",
    "/images/login/carousel-2.png",
    "/images/login/carousel-3.png",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full aspect-[859/800] max-w-[600px]">
      {images.map((src, index) => (
        <div
          key={src}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentIndex ? "opacity-100" : "opacity-0"
          }`}
        >
          <Image
            src={src}
            alt={`carousel-${index + 1}`}
            fill
            className="object-contain"
          />
        </div>
      ))}

      {/* Carousel indicators */}
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {images.map((src, index) => (
          <button
            key={src}
            type="button"
            onClick={() => setCurrentIndex(index)}
            className={`w-8 h-1 rounded-full transition-colors duration-300 ${
              index === currentIndex
                ? "bg-black"
                : "bg-black/25 hover:bg-black/75"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function AuthContent() {
  const authContext = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (authContext.status === AuthStatus.Authenticated) {
      const fetchUser = async () => {
        try {
          await fetch("/api/user/bootstrap", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });
          console.log("user bootstrap successfully");
        } catch (error) {
          throw new Error(error as string);
        } finally {
          navigate.push("/");
        }
      };

      fetchUser();
    }
  }, [authContext.status, navigate]);

  const renderStep = () => {
    // If we have an email and are authenticating, show verify form
    if (authContext.email && authContext.status === AuthStatus.Authenticating) {
      return <VerifyForm />;
    }

    // If authenticated, show completion message
    if (authContext.status === AuthStatus.Authenticated) {
      return (
        <div className="text-center">
          <LoadingSpinner />
        </div>
      );
    }

    // Default to email form
    return <EmailForm />;
  };

  const getTitle = () => {
    if (authContext.email && authContext.status === AuthStatus.Authenticating) {
      return <T messageKey="verify.hero.title" />;
    }
    return <T messageKey="login.hero.title" />;
  };

  const getDescription = () => {
    if (authContext.email && authContext.status === AuthStatus.Authenticating) {
      return (
        <T messageKey="verify.hero.description" email={authContext.email} />
      );
    }
    return <T messageKey="login.hero.description" />;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 w-full">
      <div className="gap-4 flex flex-col px-4 py-8 justify-center items-center">
        <Image
          src="/images/albert-avatar.png"
          alt="Albert avatar"
          width={100}
          height={100}
          className="rounded-2xl border border-border"
          style={{
            boxShadow: "0px 0px 100px 0px rgba(107, 109, 237, 0.50)",
          }}
        />
        <h1 className="text-center">{getTitle()}</h1>
        <p className="text-center text-lg text-heading-black -mt-4 pb-5">
          {getDescription()}
        </p>
        {renderStep()}

        <Link href="https://www.atlast.co/" target="_blank" className="mt-4">
          <Image
            src="/images/powered-by-at-last.png"
            alt="Powered by At Last"
            width={104}
            height={21}
          />
        </Link>
      </div>
      <div
        className="w-full h-full border-t border-border lg:border-t-none lg:border-l lg:w-full lg:h-screen flex flex-col justify-center items-center p-8"
        style={{
          background: `
            radial-gradient(82.36% 73.56% at 64.17% 68.05%, rgba(245, 245, 245, 0.00) 0%, rgba(249, 249, 249, 0.90) 95.67%),
            linear-gradient(118deg, rgba(207, 199, 255, 0.40) 38.58%, rgba(143, 125, 255, 0.40) 64.13%, rgba(136, 255, 217, 0.40) 93.66%)
          `,
        }}
      >
        <Carousel />
      </div>
    </div>
  );
}

export default function Auth() {
  return <AuthContent />;
}
